import * as React from 'react';
import MuiSwitch from '@mui/material/Switch';
import { makeStyles } from 'tss-react/mui';
import { FormControlLabelProps } from '@mui/material/FormControlLabel';
import { FormControlLabel } from '../form-control-label';
import { ColorPalette } from '../config';

export interface ISwitchProps {
  autoFocus?: boolean;
  checked?: boolean;
  disabled?: boolean;
  disableRipple?: boolean;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  inputRef?: React.Ref<any>;
  name?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  tabIndex?: number;
  dense?: boolean;
  label?: string;
  labelPlacement?: FormControlLabelProps['labelPlacement'];
}

const Colors = {
  disabledGray: 'rgba(0, 0, 0, 0.26)',
  activeGreen: ColorPalette.GrannySmithGreen,
};

type RuleNames =
  | 'switchBase'
  | 'switchBaseDense'
  | 'checked'
  | 'bar'
  | 'barDense'
  | 'icon'
  | 'iconDense';

const useStyles = makeStyles<void, RuleNames>()((theme, _params, classes) => ({
  root: {
    alignItems: 'center',
  },
  switchBase: {
    [`&.${classes.checked}`]: {
      color: theme.palette.common.white,
      [`& + .${classes.bar}`]: {
        backgroundColor: Colors.activeGreen,
      },
      [`& + .${classes.barDense}`]: {
        backgroundColor: Colors.activeGreen,
      },
    },
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
      easing: theme.transitions.easing.sharp,
    }),
    height: 'auto',
  },
  switchBaseDense: {
    [`&.${classes.checked}`]: {
      color: theme.palette.common.white,
      [`& + .${classes.bar}`]: {
        backgroundColor: Colors.activeGreen,
      },
      [`& + .${classes.barDense}`]: {
        backgroundColor: Colors.activeGreen,
      },
    },
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
      easing: theme.transitions.easing.sharp,
    }),
    height: 'auto',
  },
  checked: {
    transform: 'translateX(15px)',
    [`& + .${classes.bar}`]: {
      border: 'none',
      opacity: 1,
    },
    [`& + .${classes.barDense}`]: {
      border: 'none',
      opacity: 1,
    },
  },
  bar: {
    borderRadius: 13,
    width: 42,
    height: 26,
    marginTop: -13,
    marginLeft: -21,
    border: 'solid 1px',
    borderColor: theme.palette.grey[300],
    backgroundColor: theme.palette.grey[50],
    opacity: 1,
    transition: theme.transitions.create(['background-color', 'border']),
  },
  icon: {
    width: 24,
    height: 24,
  },
  iconChecked: {
    boxShadow: theme.shadows[1],
  },
  barDense: {
    borderRadius: 13,
    width: 32,
    height: 16,
    marginTop: -8,
    marginLeft: -16,
    border: 'solid 1px',
    borderColor: theme.palette.grey[300],
    backgroundColor: theme.palette.grey[50],
    opacity: 1,
    transition: theme.transitions.create(['background-color', 'border']),
  },
  iconDense: {
    width: 14,
    height: 14,
  },
  disabled: {
    [`& .${classes.switchBase}`]: {
      borderColor: Colors.disabledGray,
      [`& + .${classes.bar}`]: {
        backgroundColor: Colors.disabledGray,
        opacity: 1,
      },
      [`& + .${classes.barDense}`]: {
        backgroundColor: Colors.disabledGray,
        opacity: 1,
      },
    },
    [`& .${classes.icon}`]: {
      backgroundColor: '#fff',
      boxShadow: 'none',
    },
    [`& .${classes.iconDense}`]: {
      backgroundColor: '#fff',
      boxShadow: 'none',
    },
    [`&.${classes.checked} + .${classes.bar}`]: {
      backgroundColor: '#ddd',
    },
    [`&.${classes.checked} + .${classes.barDense}`]: {
      backgroundColor: '#ddd',
    },
    [`&.${classes.checked} .${classes.icon}`]: {
      backgroundColor: Colors.disabledGray,
    },
    [`&.${classes.checked} .${classes.iconDense}`]: {
      backgroundColor: Colors.disabledGray,
    },
  },
}));

export function Switch(props: ISwitchProps) {
  const { dense, label, labelPlacement, ...other } = props;
  const { classes } = useStyles();

  const TheSwitch = () => (
    <MuiSwitch
      classes={{
        root: classes.root,
        switchBase: dense ? classes.switchBaseDense : classes.switchBase,
        thumb: dense ? classes.iconDense : classes.icon,
        checked: classes.checked,
        disabled: classes.disabled,
      }}
      {...other}
    />
  );

  if (typeof label !== 'undefined' && label !== '') {
    return (
      <FormControlLabel
        className="switch__label"
        labelPlacement={labelPlacement}
        label={label}
        control={<TheSwitch />}
      />
    );
  }

  return <TheSwitch />;
}

Switch.displayName = 'Switch';
